(ns serv-ui.api
  "Talking to the server. EDN both ways, the key as a bearer token. A
  failed request rejects with an ex-info carrying `:status` and the
  server's `:error` and `:message`; a 401 also forgets the key, so the
  window falls back to the login."
  (:require [cljs.reader :as reader]
            [clojure.string :as str]
            [kc.core :as kc]
            [serv-ui.auth :as auth]))

(defn build
  "The version this window was released as, from its page's meta tag."
  []
  (some-> (js/document.querySelector "meta[name=kunagi-serv-build]") (.getAttribute "content")))

(def base
  "Where the API is. Served by the server itself, the window is on the same
  origin; on shadow's dev ports (50031, 50032) it is the server's port next door."
  (if (#{"50031" "50032"} js/location.port)
    (str js/location.protocol "//" js/location.hostname ":50030")
    ""))

(defonce !signed-out (atom 0))

(defn- headers [extra]
  (clj->js (cond-> extra
             (auth/signed-in?) (assoc "authorization" (str "Bearer " (auth/key))))))

(defn- fail [^js r text]
  (let [data (try (reader/read-string text) (catch :default _ nil))
        data (if (map? data) data {})]
    (when (= 401 (.-status r))
      (auth/forget-key!)
      (swap! !signed-out inc))
    (throw (ex-info (or (:message data) (str "HTTP " (.-status r)))
                    (assoc data :status (.-status r))))))

(defonce
  ^{:doc "How a request reaches the server: `js/fetch`, unless a window puts
  another in its place (the operator prompts window: `serv-ui.live`)."}
  !fetch (atom nil))

(defn- raw> [method path opts]
  ((or @!fetch js/fetch) (str base path) (clj->js (assoc opts :method (str/upper-case (name method))))))

(defn request>
  "EDN in, EDN out."
  ([method path] (request> method path nil))
  ([method path body]
   (-> (raw> method path
             (cond-> {:headers (headers (cond-> {"accept" "application/edn"}
                                          body (assoc "content-type" "application/edn")))}
               body (assoc :body (pr-str body))))
       (.then (fn [^js r]
                (-> (.text r)
                    (.then (fn [text]
                             (if (.-ok r)
                               (reader/read-string text)
                               (fail r text))))))))))

(defn get> [path] (request> :get path))

(defn data-url
  "The admin address of a path in the data directory, each segment encoded."
  [rel]
  (str "/api/admin/data/"
       (->> (str/split rel #"/") (remove str/blank?) (map js/encodeURIComponent) (str/join "/"))))

(defn file-text>
  "A file from the data directory, as text."
  [rel]
  (-> (raw> :get (data-url rel) {:headers (headers {"accept" "application/edn"})})
      (.then (fn [^js r]
               (-> (.text r)
                   (.then (fn [text] (if (.-ok r) text (fail r text)))))))))

(defn file-blob>
  [rel]
  (-> (raw> :get (data-url rel) {:headers (headers {"accept" "application/edn"})})
      (.then (fn [^js r] (if (.-ok r) (.blob r) (.then (.text r) #(fail r %)))))))

(defn put-text>
  [rel text]
  (-> (raw> :put (data-url rel) {:headers (headers {"accept" "application/edn"}) :body text})
      (.then (fn [^js r]
               (-> (.text r)
                   (.then (fn [t] (if (.-ok r) (reader/read-string t) (fail r t)))))))))

(defn delete> [rel] (request> :delete (data-url rel)))

(defn blob>
  "Any API path, as a Blob."
  [path]
  (-> (raw> :get path {:headers (headers {"accept" "application/edn"})})
      (.then (fn [^js r] (if (.-ok r) (.blob r) (.then (.text r) #(fail r %)))))))

(defn form>
  "FormData in, EDN out."
  [method path ^js form]
  (-> (raw> method path {:headers (headers {"accept" "application/edn"}) :body form})
      (.then (fn [^js r]
               (-> (.text r)
                   (.then (fn [t] (if (.-ok r) (reader/read-string t) (fail r t)))))))))

(defn use-load
  "Load `path` (EDN) whenever it changes. Answers `{:data :error :reload}`."
  [path]
  (let [[state set-state] (kc/use-state {})
        [n set-n]         (kc/use-state 0)]
    (kc/use-effect
     [path n]
     (let [live? (volatile! true)]
       (set-state (fn [s] (dissoc s :error)))
       (-> (get> path)
           (.then #(when @live? (set-state {:data %})))
           (.catch #(when @live? (set-state {:error (or (ex-message %) (str %))
                                             :status (:status (ex-data %))}))))
       (fn [] (vreset! live? false))))
    (assoc state :reload #(set-n inc))))

(defn bytes-str
  "1234567 -> \"1.2 MB\"."
  [n]
  (let [n (or n 0)]
    (cond (< n 1024)            (str n " B")
          (< n (* 1024 1024))   (str (.toFixed (/ n 1024) 1) " KB")
          (< n (* 1024 1024 1024)) (str (.toFixed (/ n 1024 1024) 1) " MB")
          :else                 (str (.toFixed (/ n 1024 1024 1024) 1) " GB"))))

(defn date-str
  "An ISO timestamp, as a local date and time without seconds."
  [iso]
  (if-let [d (some-> iso js/Date.)]
    (.toLocaleString d js/undefined #js {:dateStyle "medium" :timeStyle "short"})
    ""))
